/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Subsets", description = "Subsets allow organizations to segment their total addressable audience data based on its attributes and then grant employees access to that data based on the segment.", lastUpdatedDate = new Date(1716249600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Subsets",
        description: "Subsets allow organizations to segment their total addressable audience data based on its attributes and then grant employees access to that data based on the segment.",
        lastUpdatedDate: "Tue May 21 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "What are subsets?",
          "depth": 0,
          "id": "what-are-subsets"
        }, {
          "value": "Common use cases for subsets",
          "depth": 1,
          "id": "common-use-cases-for-subsets"
        }, {
          "value": "Subset categories vs. subsets",
          "depth": 1,
          "id": "subset-categories-vs-subsets"
        }, {
          "value": "Setting up subsets",
          "depth": 0,
          "id": "setting-up-subsets"
        }, {
          "value": "Creating and defining a subset category",
          "depth": 1,
          "id": "creating-and-defining-a-subset-category"
        }, {
          "value": "Creating and defining a subset",
          "depth": 1,
          "id": "creating-and-defining-a-subset"
        }, {
          "value": "Granting access to a subset",
          "depth": 1,
          "id": "granting-access-to-a-subset"
        }, {
          "value": "Using subsets in audience creation",
          "depth": 0,
          "id": "using-subsets-in-audience-creation"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      h3: "h3",
      ul: "ul",
      li: "li",
      em: "em",
      a: "a",
      img: "img",
      code: "code"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "what-are-subsets",
        children: "What are subsets?"
      }), "\n", _jsx(_components.p, {
        children: "Subsets allow organizations to segment their total addressable audience data\nbased on its attributes and then grant employees access to that data based on\nthe segment. For example, some organizations may have a Global Customers table\nbut only want employees to be able to access data in Hightouch from the region\nin which they work. By creating a subset for each region, companies can assign\nspecific employees to each region and limit the data each employee can view and\nactivate."
      }), "\n", _jsx(_components.h3, {
        id: "common-use-cases-for-subsets",
        children: "Common use cases for subsets"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.em, {
            children: "Consent"
          }), ": Some companies may collect consent or opt-ins for different marketing use\ncases. Companies can use subsets to create different filters for each\nconsent type. For example, lifecycle marketers may be assigned to an “Email\nOpt-Ins” subset, while performance marketers may be assigned to a subset for\n“Targetable Customers.”"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.em, {
            children: "Region"
          }), ": In multinational corporations, subsets can help scope employee data access\nto data within a particular geography or region."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.em, {
            children: "Brand"
          }), ": For companies with multiple business units, brands, or product lines,\nsubsets can be applied so that employees can only create audiences with data\nscoped to their specific business unit, brand, or product."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "subset-categories-vs-subsets",
        children: "Subset categories vs. subsets"
      }), "\n", _jsx(_components.p, {
        children: "A parent model can be segmented with subset categories and subsets. In the above\nexample, region is the subset category and each region (North America, EMEA,\nAPAC) is the subset."
      }), "\n", _jsx(_components.p, {
        children: "When building an audience, you can select more than one subset category and more\nthan one subset within each subset category. A user must be part of any subset\nwithin a subset category, and satisfy all subset categories to be part of the\naudience."
      }), "\n", _jsx(_components.h2, {
        id: "setting-up-subsets",
        children: "Setting up subsets"
      }), "\n", _jsxs(_components.p, {
        children: ["To configure your subsets, go to the ", _jsx(_components.a, {
          href: "https://app.hightouch.com/governance/subset-categories",
          children: "governance page"
        })]
      }), "\n", _jsx(_components.h3, {
        id: "creating-and-defining-a-subset-category",
        children: "Creating and defining a subset category"
      }), "\n", _jsx(_components.p, {
        children: "The first step in defining a subset is to create a subset category. A subset\ncategory groups multiple subsets together. For example, you may have a Region\ncategory that has separate subsets for US, EU, and EMEA."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/subsets/subset-categories.png",
          alt: "Subset categories"
        })
      }), "\n", _jsx(_components.p, {
        children: "To create a new category, click Add Category. Name your category and provide a\ndescription."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/subsets/new-subset-category.png",
          alt: "New subset category"
        })
      }), "\n", _jsx(_components.p, {
        children: "Once a category has been created, you can choose whether to make this category\nrequired. When a category is required, users must apply a subset from within\nthat category when creating a new audience. If a user does not have access to\nany of the subsets in a required category, they will not be able to build any\naudiences."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/subsets/subset-category.png",
          alt: "Subset category"
        })
      }), "\n", _jsx(_components.p, {
        children: "Once created, click Add subset to create a subset within that category."
      }), "\n", _jsx(_components.h3, {
        id: "creating-and-defining-a-subset",
        children: "Creating and defining a subset"
      }), "\n", _jsxs(_components.p, {
        children: ["To create your subset, you can mix and match different conditions to segment\nyour parent model to a specific filter. Similar to building an audience, the\nbuilder provides five types of filters you can use\nwith ", _jsxs(_components.a, {
          href: "https://hightouch.com/docs/customer-studio/usage#nested-conditions",
          children: ["nested Boolean (", _jsx(_components.code, {
            children: "AND"
          }), " / ", _jsx(_components.code, {
            children: "OR"
          }), ") logic"]
        }), ":"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Property conditions"
        }), "\n", _jsx(_components.li, {
          children: "Related model conditions"
        }), "\n", _jsx(_components.li, {
          children: "Event conditions"
        }), "\n", _jsx(_components.li, {
          children: "Audience conditions"
        }), "\n", _jsx(_components.li, {
          children: "Trait conditions"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/subsets/new-subset.png",
          alt: "New subset"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["For more information on how to use the builder UI, see this section\n", _jsx(_components.a, {
          href: "https://hightouch.com/docs/customer-studio/usage#filter-conditions#filter-conditions",
          children: "here"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "granting-access-to-a-subset",
        children: "Granting access to a subset"
      }), "\n", _jsx(_components.p, {
        children: "Once your filter conditions have been created, select which users should have\naccess to this subset."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/subsets/subset-access.png",
          alt: "Subset access"
        })
      }), "\n", _jsx(_components.h2, {
        id: "using-subsets-in-audience-creation",
        children: "Using subsets in audience creation"
      }), "\n", _jsx(_components.p, {
        children: "Once a user is granted access to a specific subset, they can select the subset\nin the dropdown of the base filters section of the audience builder. If a user\ndoes not have access to a specific subset, it will be greyed out."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/subsets/base-filters.png",
          alt: "Subsets"
        })
      }), "\n", _jsx(_components.p, {
        children: "In the above example, these base filters will allow any user has purchased from\nthe Sketchers brand AND is part of the Australia OR Asia regions to be in this\naudience."
      })]
    });
  }
}
export default MDXContent;
